// src/components/Gemini.js
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import Avatar from './Avatar';
import '../styles/Gemini.css';

const Gemini = ({ user }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm Velvet AI, your AI assistant. How can I help you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post('/api/gemini/chat', {
        message: userMessage,
        conversation_history: messages
      });

      // Add assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response || response.data.message
      }]);
    } catch (error) {
      console.error('Error calling Gemini API:', error);

      // For now, show a placeholder response since API isn't set up yet
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm not connected to the Velvet AI API yet. This is a placeholder response. Once the API is configured, I'll be able to help you with various tasks!"
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const clearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: "Hello! I'm Velvet AI, your AI assistant. How can I help you today?"
      }
    ]);
  };

  return (
    <div className="gemini-container">
      <div className="gemini-header">
        <div className="gemini-title">
          <Icon name="ai" size="1.5rem" />
          <h2>Velvet Chat</h2>
        </div>
        <button className="gemini-clear-btn" onClick={clearChat} title="Clear conversation">
          <Icon name="trash-empty" size="1rem" />
          <span>Clear</span>
        </button>
      </div>

      <div className="gemini-messages">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`gemini-message ${message.role === 'user' ? 'user' : 'assistant'}`}
          >
            <div className="message-avatar">
              {message.role === 'user' ? (
                <Avatar user={user} size="sm" fallbackType="icon" />
              ) : (
                <div className="gemini-avatar">
                  <Icon name="ai" size="1rem" />
                </div>
              )}
            </div>
            <div className="message-content">
              <div className="message-role">
                {message.role === 'user' ? user?.name || 'You' : 'Velvet AI'}
              </div>
              <div className="message-text">{message.content}</div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="gemini-message assistant">
            <div className="message-avatar">
              <div className="gemini-avatar">
                <Icon name="ai" size="1rem" />
              </div>
            </div>
            <div className="message-content">
              <div className="message-role">Velvet AI</div>
              <div className="message-text typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="gemini-input-form" onSubmit={handleSubmit}>
        <div className="gemini-input-wrapper">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message Velvet AI..."
            rows="1"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="gemini-send-btn"
            disabled={!inputValue.trim() || isLoading}
          >
            <Icon name="paper-plane-empty" size="1.25rem" />
          </button>
        </div>
        <div className="gemini-input-hint">
          Press Enter to send, Shift+Enter for new line
        </div>
      </form>
    </div>
  );
};

export default Gemini;
